import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, authHeadersOnly } from '../services/apiConfig';
import './MyOrders.css';
import OrderDetails from './OrderDetails';
import { FaRupeeSign, FaSearch, FaBox, FaShippingFast, FaTruck,
         FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaCalendarAlt,
         FaMapMarkerAlt, FaChevronRight, FaLayerGroup } from 'react-icons/fa';

const STATUS = {
  PENDING:          { label: 'Pending',         color: '#d97706', bg: '#fef3c7', dot: '#f59e0b' },
  CONFIRMED:        { label: 'Confirmed',        color: '#1d4ed8', bg: '#dbeafe', dot: '#3b82f6' },
  PACKED:           { label: 'Packed',           color: '#6d28d9', bg: '#ede9fe', dot: '#8b5cf6' },
  SHIPPED:          { label: 'Shipped',          color: '#0e7490', bg: '#cffafe', dot: '#06b6d4' },
  OUT_FOR_DELIVERY: { label: 'Out for Delivery', color: '#065f46', bg: '#d1fae5', dot: '#10b981' },
  DELIVERED:        { label: 'Delivered',        color: '#166534', bg: '#dcfce7', dot: '#22c55e' },
  CANCELLED:        { label: 'Cancelled',        color: '#991b1b', bg: '#fee2e2', dot: '#ef4444' },
  RETURNED:         { label: 'Returned',         color: '#374151', bg: '#f3f4f6', dot: '#9ca3af' },
};

const STEPS = ['PENDING','CONFIRMED','PACKED','SHIPPED','OUT_FOR_DELIVERY','DELIVERED'];
const TABS  = ['ALL','PENDING','SHIPPED','DELIVERED','CANCELLED'];

const resolveImg = (url) => {
  if (!url) return '/images/product.jpg';
  return url.startsWith('http') ? url : `${API}${url}`;
};

const parseItems = (str) => { try { return JSON.parse(str || '[]'); } catch { return []; } };

const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const fmtAmt = (n) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MyOrders({ user, onClose, embedded }) {
  const [allOrders, setAllOrders]   = useState([]);
  const [shown, setShown]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(0);
  const [pages, setPages]           = useState(0);
  const [query, setQuery]           = useState('');
  const [tab, setTab]               = useState('ALL');
  const [selected, setSelected]     = useState(null);
  const [deleting, setDeleting]      = useState(null);
  const PER = 6;

  useEffect(() => { load(); }, []);
  useEffect(() => { filter(allOrders, tab, query, 0); setPage(0); }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/orders/user/${user.userId}`, { headers: authHeadersOnly() });
      setAllOrders(data || []);
      filter(data || [], tab, query, 0);
    } catch { setAllOrders([]); setShown([]); }
    finally { setLoading(false); }
  };

  const filter = (data, t, q, p) => {
    let f = t === 'ALL' ? data : data.filter(o => o.status === t);
    if (q.trim()) f = f.filter(o =>
      o.id.toString().includes(q.trim()) ||
      parseItems(o.items).some(i => i.productName?.toLowerCase().includes(q.toLowerCase()))
    );
    setPages(Math.ceil(f.length / PER));
    setShown(f.slice(p * PER, p * PER + PER));
  };

  const goPage = (p) => { setPage(p); filter(allOrders, tab, query, p); };

  const deleteOrder = async (e, orderId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this order from your history?')) return;
    setDeleting(orderId);
    try {
      await axios.delete(`${API}/api/orders/${orderId}?userId=${user.userId}`, { headers: authHeadersOnly() });
      const updated = allOrders.filter(o => o.id !== orderId);
      setAllOrders(updated);
      filter(updated, tab, query, page);
    } catch {
      alert('Failed to delete order.');
    } finally {
      setDeleting(null);
    }
  };

  const counts = TABS.reduce((a, t) => {
    a[t] = t === 'ALL' ? allOrders.length : allOrders.filter(o => o.status === t).length;
    return a;
  }, {});

  if (selected) return (
    <div className={embedded ? 'mo-wrap-emb' : 'mo-wrap'}>
      {!embedded && <div className="mo-backdrop" onClick={onClose} />}
      <div className={embedded ? 'mo-panel-emb' : 'mo-panel'}>
        <OrderDetails order={selected} user={user} onBack={() => setSelected(null)} />
      </div>
    </div>
  );

  return (
    <div className={embedded ? 'mo-wrap-emb' : 'mo-wrap'}>
      {!embedded && <div className="mo-backdrop" onClick={onClose} />}
      <div className={embedded ? 'mo-panel-emb' : 'mo-panel'}>

        {/* ── Header ── */}
        <div className="mo-hdr">
          <div className="mo-hdr-left">
            <div className="mo-hdr-icon"><FaLayerGroup /></div>
            <div>
              <h2>My Orders</h2>
              <span>{allOrders.length} total order{allOrders.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <button className="mo-hdr-close" onClick={onClose}>{embedded ? '← Back' : '×'}</button>
        </div>

        {/* ── Tabs ── */}
        <div className="mo-tabs-bar">
          {TABS.map(t => (
            <button key={t} className={`mo-tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>
              {t === 'ALL' ? 'All Orders' : STATUS[t]?.label}
              {counts[t] > 0 && <em>{counts[t]}</em>}
            </button>
          ))}
        </div>

        {/* ── Search ── */}
        <div className="mo-search-bar">
          <div className="mo-search-box">
            <FaSearch />
            <input
              placeholder="Search by product name…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (filter(allOrders, tab, query, 0), setPage(0))}
            />
            {query && <button onClick={() => { setQuery(''); filter(allOrders, tab, '', 0); setPage(0); }}>×</button>}
          </div>
          <select value={tab} onChange={e => setTab(e.target.value)} className="mo-sel">
            {['ALL','PENDING','CONFIRMED','PACKED','SHIPPED','OUT_FOR_DELIVERY','DELIVERED','CANCELLED'].map(t => (
              <option key={t} value={t}>{t === 'ALL' ? 'All Status' : STATUS[t]?.label}</option>
            ))}
          </select>
        </div>

        {/* ── Body ── */}
        <div className="mo-body">
          {loading ? (
            <div className="mo-state"><div className="mo-spin" /><p>Loading your orders…</p></div>
          ) : shown.length === 0 ? (
            <div className="mo-state mo-empty">
              <span className="mo-empty-ico">📦</span>
              <h3>No orders found</h3>
              <p>{tab !== 'ALL' ? `No ${STATUS[tab]?.label.toLowerCase()} orders yet.` : "You haven't placed any orders yet."}</p>
            </div>
          ) : (
            <div className="mo-grid">
              {shown.map(order => {
                const items = parseItems(order.items);
                const cfg   = STATUS[order.status] || STATUS.PENDING;
                const stepIdx = STEPS.indexOf(order.status);
                const cancelled = ['CANCELLED','RETURNED'].includes(order.status);

                return (
                  <div key={order.id} className="mo-card" onClick={() => setSelected(order)}>

                    {/* Status ribbon */}
                    <div className="mo-card-ribbon" style={{ background: cfg.dot }} />

                    {/* Images row */}
                    <div className="mo-card-imgs">
                      {items.slice(0, 3).map((it, i) => (
                        <div key={i} className="mo-card-img-slot" style={{ zIndex: 3 - i, marginLeft: i > 0 ? '-14px' : 0 }}>
                          <img
                            src={resolveImg(it.image)}
                            alt={it.productName}
                            onError={e => { e.target.src = '/images/product.jpg'; }}
                          />
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div className="mo-card-img-more">+{items.length - 3}</div>
                      )}
                      <span className="mo-card-status-pill" style={{ color: cfg.color, background: cfg.bg }}>
                        {cfg.label}
                      </span>
                    </div>

                    {/* Product name */}
                    <div className="mo-card-name">
                      {items[0]?.productName || 'Order'}
                      {items.length > 1 && <span className="mo-card-extra"> +{items.length - 1} more</span>}
                    </div>

                    {/* Meta */}
                    <div className="mo-card-meta">
                      <span><FaCalendarAlt /> {fmtDate(order.createdAt)}</span>
                      <span className="mo-card-amt"><FaRupeeSign />{fmtAmt(order.totalAmount)}</span>
                    </div>

                    {order.shippingAddress && (
                      <div className="mo-card-addr">
                        <FaMapMarkerAlt />
                        <span>{order.shippingAddress.substring(0, 48)}{order.shippingAddress.length > 48 ? '…' : ''}</span>
                      </div>
                    )}

                    {/* Progress stepper */}
                    {!cancelled && (
                      <div className="mo-stepper">
                        {STEPS.map((s, i) => (
                          <React.Fragment key={s}>
                            <div
                              className={`mo-step-dot ${i < stepIdx ? 'done' : ''} ${i === stepIdx ? 'active' : ''}`}
                              title={STATUS[s]?.label}
                            />
                            {i < STEPS.length - 1 && (
                              <div className={`mo-step-line ${i < stepIdx ? 'done' : ''}`} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {cancelled && (
                      <div className="mo-cancelled-bar">
                        <FaTimesCircle /> Order {cfg.label}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="mo-card-footer">
                      <span className="mo-card-items-count">
                        <FaBox /> {items.length} item{items.length !== 1 ? 's' : ''}
                      </span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="mo-card-btn">
                          Details <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {pages > 1 && (
            <div className="mo-pages">
              <button onClick={() => goPage(page - 1)} disabled={page === 0}>‹</button>
              {Array.from({ length: pages }, (_, i) => (
                <button key={i} className={page === i ? 'on' : ''} onClick={() => goPage(i)}>{i + 1}</button>
              ))}
              <button onClick={() => goPage(page + 1)} disabled={page === pages - 1}>›</button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
